import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ReceiptLong as ReceiptLongIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { vouchersApi, formatDate, formatDateTime, formatTotals } from '../../services/expenseVouchersApi';
import { useExpenseBase, VoucherStatusChip } from '../../components/expense/voucherShared';
import { PAYMENT_STATUS_META } from '../../services/expenseAccountsApi';
import { CompactFilterBar } from '../../components/expense/AccountsFilters';

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT_BACK', label: 'Sent Back' },
  { value: 'PENDING_ACCOUNTS', label: 'Pending Accounts' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

// Dashboard links open this list pre-filtered: ?status=PENDING_ACCOUNTS or
// ?paymentStatus=PAID,ON_HOLD (approved vouchers in those payment statuses; Phase 5B-2).
const initialStatus = (params) => {
  const s = params.get('status') || '';
  return STATUS_FILTERS.some((f) => f.value === s) ? s : '';
};
const initialPaymentStatus = (params) => (params.get('paymentStatus') || '')
  .split(',').filter((p) => PAYMENT_STATUS_META[p]).join(',');

// Employee "My Expenses": the signed-in employee's own vouchers (server-scoped).
const MyExpenses = () => {
  const navigate = useNavigate();
  const base = useExpenseBase();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState(() => initialStatus(searchParams));
  const [paymentStatus, setPaymentStatus] = useState(() => initialPaymentStatus(searchParams));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, status, paymentStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.list({ search: debouncedSearch, status, paymentStatus, page: page + 1, limit: rowsPerPage });
      setRows(res.data || []);
      setTotal(res.pagination?.totalItems || 0);
      setLoadError(null);
    } catch (e) {
      setRows([]);
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, paymentStatus, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete draft ${row.voucherNumber}? This cannot be undone.`)) return;
    try {
      const res = await vouchersApi.remove(row._id);
      toast.success(res.message || 'Draft deleted');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const hasFilters = Boolean(debouncedSearch || status || paymentStatus);

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon color="primary" />
            My Expenses
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your expense vouchers. Sent back vouchers are highlighted and need editing.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${base}/new`)} sx={{ textTransform: 'none' }}>
          Create Expense
        </Button>
      </Box>

      {loadError?.status === 403 ? (
        <Alert severity="warning">{loadError.message} Please contact HR if you need Expense access.</Alert>
      ) : (
        <>
          <CompactFilterBar>
            <TextField
              size="small"
              placeholder="Search voucher no., invoice, vendor, description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: 180 }}
              SelectProps={{ inputProps: { 'data-testid': 'status-filter' } }}
            >
              {STATUS_FILTERS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            {paymentStatus && (
              <Chip
                label={`Payment: ${paymentStatus.split(',').map((p) => PAYMENT_STATUS_META[p].label).join(', ')}`}
                onDelete={() => setPaymentStatus('')}
                sx={{ alignSelf: 'center' }}
                data-testid="payment-status-filter"
              />
            )}
          </CompactFilterBar>

          {loadError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load vouchers: {loadError.message}</Alert>}

          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  {['Voucher No.', 'Date', 'Vendor', 'Total', 'Status', 'Last Updated', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center"><CircularProgress size={28} /></TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {hasFilters ? 'No vouchers match your filters.' : 'You have no expense vouchers yet.'}
                      </Typography>
                      {!hasFilters && !loadError && (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => navigate(`${base}/new`)} sx={{ textTransform: 'none' }}>
                          Create your first expense
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : rows.map((row) => (
                  <TableRow
                    key={row._id}
                    hover
                    sx={row.status === 'SENT_BACK' ? { bgcolor: 'rgba(255, 193, 7, 0.12)' } : undefined}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.voucherNumber}</TableCell>
                    <TableCell>{formatDate(row.voucherDate)}</TableCell>
                    <TableCell>{row.vendors?.length ? row.vendors.join(', ') : '-'}</TableCell>
                    <TableCell>{formatTotals(row)}</TableCell>
                    <TableCell><VoucherStatusChip status={row.status} /></TableCell>
                    <TableCell>{formatDateTime(row.updatedAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View">
                          <IconButton size="small" color="primary" aria-label={`View ${row.voucherNumber}`} onClick={() => navigate(`${base}/${row._id}`)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.canEdit && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" aria-label={`Edit ${row.voucherNumber}`} onClick={() => navigate(`${base}/${row._id}/edit`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.canDelete && (
                          <Tooltip title="Delete draft">
                            <IconButton size="small" color="error" aria-label={`Delete ${row.voucherNumber}`} onClick={() => handleDelete(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default MyExpenses;
